/**
 * LoginPage - authentification standard : Mot de passe + Code OTP par email.
 *
 * Vues :
 *  - 'credentials' : email + mot de passe -> POST /auth/login
 *      -> step 'completed'    (appareil de confiance) : session posee, on entre
 *      -> step 'otp_required' : bascule vers la vue OTP
 *  - 'otp'         : code a 6 chiffres + "se souvenir de cet appareil" (30j)
 *  - 'forgot'      : email -> lien "definir / reinitialiser mon mot de passe"
 *
 * La 1re connexion (compte sans mot de passe) passe par "mot de passe oublie"
 * qui envoie un lien de definition. Le magic-link n'est plus une methode de
 * connexion courante (reserve invitation + reset).
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button, Input, Label, Body1, Spinner, Checkbox, tokens,
} from '@fluentui/react-components';
import { useAuth } from '../auth/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

type View = 'credentials' | 'otp' | 'forgot' | 'forgotSent';

const cardWrap: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
};

export function LoginPage() {
  const { t } = useTranslation();
  const { login, verifyLoginOtp, requestPasswordReset } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';

  const [view, setView]         = useState<View>('credentials');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState<string | null>(null);

  // OTP state
  const [challengeId, setChallengeId] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [code, setCode]               = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);

  async function onSubmitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await login(email.trim(), password);
      if (res.step === 'completed') {
        navigate(redirectTo, { replace: true });
      } else {
        setChallengeId(res.challengeId);
        setMaskedEmail(res.maskedEmail);
        setCode('');
        setView('otp');
      }
    } catch (err) {
      setError((err as Error)?.message ?? t('auth.login.errorGeneric', 'Connexion impossible.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyLoginOtp(challengeId, code.trim(), rememberDevice);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError((err as Error)?.message ?? t('auth.otp.errorGeneric', 'Code invalide.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onResendCode() {
    setError(null);
    setSubmitting(true);
    try {
      const res = await login(email.trim(), password);
      if (res.step === 'otp_required') {
        setChallengeId(res.challengeId);
        setMaskedEmail(res.maskedEmail);
        setCode('');
      }
    } catch (err) {
      setError((err as Error)?.message ?? t('auth.otp.errorGeneric', 'Code invalide.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitForgot(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
      setView('forgotSent');
    } catch (err) {
      setError((err as Error)?.message ?? t('auth.forgot.errorGeneric', 'Envoi impossible.'));
    } finally {
      setSubmitting(false);
    }
  }

  const errorBox = error && (
    <div style={{
      padding: 12, background: tokens.colorPaletteRedBackground1,
      borderLeft: `4px solid ${tokens.colorPaletteRedBorder2}`, borderRadius: 6,
      fontSize: 13, color: tokens.colorPaletteRedForeground2,
    }}>{error}</div>
  );

  return (
    <div style={{
      ...cardWrap,
      background: `linear-gradient(135deg, ${tokens.colorNeutralBackground2} 0%, ${tokens.colorNeutralBackground3} 100%)`,
    }}>
      <div style={{
        width: '100%', maxWidth: 420, padding: '40px 32px',
        background: tokens.colorNeutralBackground1, borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)', border: `1px solid ${tokens.colorNeutralStroke2}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <LanguageSwitcher />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: 28, gap: 14 }}>
          <BrandLogo height={64} />
          <Body1 style={{ color: tokens.colorNeutralForeground3, maxWidth: 320 }}>
            {t('auth.login.subtitle', 'Connectez-vous à votre espace de conformité.')}
          </Body1>
        </div>

        {/* ---- Vue identifiants ---- */}
        {view === 'credentials' && (
          <form onSubmit={onSubmitCredentials} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <Label htmlFor="email" required>{t('auth.login.emailLabel', 'Adresse email')}</Label>
              <Input id="email" type="email" required value={email}
                onChange={(_e, d) => setEmail(d.value)}
                placeholder={t('auth.login.emailPlaceholder', 'vous@organisation.be')}
                style={{ width: '100%', marginTop: 4 }} />
            </div>
            <div>
              <Label htmlFor="password" required>{t('auth.login.passwordLabel', 'Mot de passe')}</Label>
              <Input id="password" type="password" required value={password}
                onChange={(_e, d) => setPassword(d.value)} placeholder="••••••••••"
                style={{ width: '100%', marginTop: 4 }} />
            </div>
            {errorBox}
            <Button type="submit" appearance="primary" size="large"
              disabled={submitting || !email || !password}
              icon={submitting ? <Spinner size="tiny" /> : undefined}>
              {submitting ? t('auth.login.submitting', 'Connexion…') : t('auth.login.submit', 'Se connecter')}
            </Button>
            <Button appearance="transparent" size="small"
              onClick={() => { setError(null); setView('forgot'); }}
              style={{ alignSelf: 'center' }}>
              {t('auth.forgot.link', 'Mot de passe oublié / Première connexion ?')}
            </Button>
          </form>
        )}

        {/* ---- Vue OTP ---- */}
        {view === 'otp' && (
          <form onSubmit={onSubmitOtp} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{
              padding: 14, background: tokens.colorNeutralBackground2,
              borderRadius: 8, fontSize: 14, color: tokens.colorNeutralForeground2,
            }}>
              {t('auth.otp.intro', 'Un code de vérification à 6 chiffres a été envoyé à')}{' '}
              <strong>{maskedEmail}</strong>.
            </div>
            <div>
              <Label htmlFor="otp" required>{t('auth.otp.codeLabel', 'Code de vérification')}</Label>
              <Input id="otp" required value={code}
                onChange={(_e, d) => setCode(d.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000" inputMode="numeric"
                style={{ width: '100%', marginTop: 4, letterSpacing: 6, fontSize: 20, textAlign: 'center' }} />
            </div>
            <Checkbox checked={rememberDevice}
              onChange={(_e, d) => setRememberDevice(Boolean(d.checked))}
              label={t('auth.otp.remember', 'Se souvenir de cet appareil pendant 30 jours')} />
            {errorBox}
            <Button type="submit" appearance="primary" size="large"
              disabled={submitting || code.length !== 6}
              icon={submitting ? <Spinner size="tiny" /> : undefined}>
              {submitting ? t('auth.otp.verifying', 'Vérification…') : t('auth.otp.submit', 'Vérifier et se connecter')}
            </Button>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button appearance="transparent" size="small" disabled={submitting} onClick={onResendCode}>
                {t('auth.otp.resend', 'Renvoyer le code')}
              </Button>
              <Button appearance="transparent" size="small"
                onClick={() => { setError(null); setCode(''); setView('credentials'); }}>
                {t('auth.otp.back', 'Retour')}
              </Button>
            </div>
          </form>
        )}

        {/* ---- Vue mot de passe oublié ---- */}
        {view === 'forgot' && (
          <form onSubmit={onSubmitForgot} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
              {t('auth.forgot.intro', 'Saisissez votre email : vous recevrez un lien pour définir ou réinitialiser votre mot de passe.')}
            </Body1>
            <div>
              <Label htmlFor="femail" required>{t('auth.login.emailLabel', 'Adresse email')}</Label>
              <Input id="femail" type="email" required value={email}
                onChange={(_e, d) => setEmail(d.value)}
                placeholder={t('auth.login.emailPlaceholder', 'vous@organisation.be')}
                style={{ width: '100%', marginTop: 4 }} />
            </div>
            {errorBox}
            <Button type="submit" appearance="primary" size="large"
              disabled={submitting || !email}
              icon={submitting ? <Spinner size="tiny" /> : undefined}>
              {submitting ? t('auth.forgot.submitting', 'Envoi…') : t('auth.forgot.submit', 'Envoyer le lien')}
            </Button>
            <Button appearance="transparent" size="small"
              onClick={() => { setError(null); setView('credentials'); }} style={{ alignSelf: 'center' }}>
              {t('auth.otp.back', 'Retour')}
            </Button>
          </form>
        )}

        {/* ---- Confirmation envoi lien ---- */}
        {view === 'forgotSent' && (
          <div style={{
            padding: 16, background: tokens.colorPaletteGreenBackground1,
            borderLeft: `4px solid ${tokens.colorPaletteGreenBorder2}`, borderRadius: 6,
          }}>
            <strong>{t('auth.forgot.sentTitle', 'Vérifiez votre boîte mail')}</strong>
            <Body1 block style={{ marginTop: 6 }}>
              {t('auth.forgot.sentBody', 'Si un compte existe pour cet email, un lien pour définir votre mot de passe vient de partir (pensez à vérifier les spams).')}
            </Body1>
            <Button appearance="subtle" onClick={() => setView('credentials')} style={{ marginTop: 12, padding: 0 }}>
              {t('auth.otp.back', 'Retour')}
            </Button>
          </div>
        )}

        <div style={{
          marginTop: 24, paddingTop: 16, borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
          textAlign: 'center', fontSize: 13, color: tokens.colorNeutralForeground3,
        }}>
          {t('auth.login.noAccount', 'Pas encore de compte ?')}{' '}
          <a href="https://nis365.eu/signup" style={{ color: tokens.colorBrandForeground1, fontWeight: 600, textDecoration: 'none' }}>
            {t('auth.login.signupLink', 'Créer un compte')}
          </a>
        </div>
      </div>
    </div>
  );
}
